import React, { useState, useEffect } from 'react';
import { Product } from '../types';
import { useCart } from '../hooks/useCart';

const EMOJI: Record<string, string> = {
  protection:'🛡️', tests:'🔬', 'soins-intimes':'💊',
};

interface Props { product: Product | null; onClose: () => void; }

export default function ProductModal({ product, onClose }: Props) {
  const { dispatch } = useCart();
  const [qty,   setQty]   = useState(product?.min_quantity || 1);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    setQty(product?.min_quantity || 1); setAdded(false);
    if (product) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed'; // évite scroll background iOS
      document.body.style.width    = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width    = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width    = '';
    };
  }, [product]);

  if (!product) return null;

  const emoji    = EMOJI[product.categories?.slug || ''] || '📦';
  const price    = Number(product.price).toLocaleString('fr-FR');
  const longDesc = product.long_description || product.description || '';

  const handleAdd = () => {
    for (let i = 0; i < qty; i++) dispatch({ type: 'ADD', product });
    setAdded(true);
    setTimeout(() => { setAdded(false); onClose(); }, 900);
  };

  return (
    <>
      <style>{`
        @keyframes slideUpModal { from{transform:translateY(100%)} to{transform:translateY(0)} }
        @keyframes fadeOverlay  { from{opacity:0} to{opacity:1} }
      `}</style>

      {/* Overlay */}
      <div onClick={onClose} style={{
        position:'fixed', inset:0,
        background:'rgba(0,0,0,0.7)',
        zIndex:300,
        animation:'fadeOverlay 0.2s ease',
        WebkitAnimation:'fadeOverlay 0.2s ease',
      }} />

      {/* Sheet — monte depuis le bas sur mobile, centré sur desktop */}
      <div style={{
        position:'fixed',
        bottom:0, left:0, right:0,
        zIndex:301,
        background:'var(--surface)',
        borderRadius:'18px 18px 0 0',
        maxHeight:'92dvh',
        display:'flex', flexDirection:'column',
        animation:'slideUpModal 0.32s cubic-bezier(0.4,0,0.2,1)',
        WebkitAnimation:'slideUpModal 0.32s cubic-bezier(0.4,0,0.2,1)',
        /* Desktop: centré */
      }}>
        {/* Drag handle */}
        <div style={{ display:'flex', justifyContent:'center', padding:'0.6rem 0 0' }}>
          <div style={{ width:'2.5rem', height:'4px', borderRadius:'2px', background:'var(--border)' }} />
        </div>

        {/* Scrollable content */}
        <div style={{ flex:1, overflowY:'auto', WebkitOverflowScrolling:'touch' as any }}>
          {/* Image */}
          <div style={{
            position:'relative', aspectRatio:'16/9', flexShrink:0,
            background:'var(--surface2)',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:'5rem', overflow:'hidden',
          }}>
            {product.image_url
              ? <img src={product.image_url} alt={product.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
              : <span>{emoji}</span>
            }
            <button onClick={onClose} style={{
              position:'absolute', top:'0.75rem', right:'0.75rem',
              background:'rgba(8,10,12,0.75)', backdropFilter:'blur(8px)',
              border:'1px solid var(--border)', color:'var(--text)',
              width:'2.2rem', height:'2.2rem', borderRadius:'50%',
              fontSize:'0.9rem', display:'flex', alignItems:'center', justifyContent:'center',
            }}>✕</button>
            {product.categories && (
              <span style={{
                position:'absolute', bottom:'0.75rem', left:'0.75rem',
                background:'rgba(8,10,12,0.8)', backdropFilter:'blur(8px)',
                border:'1px solid var(--border)', borderRadius:'2rem',
                padding:'0.2rem 0.65rem', fontSize:'0.62rem',
                letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--accent)',
              }}>
                {product.categories.name}
              </span>
            )}
          </div>

          {/* Infos */}
          <div style={{ padding:'1.1rem var(--page-px) 0' }}>
            <h2 style={{
              fontFamily:"'Cormorant Garamond',serif",
              fontSize:'1.5rem', fontWeight:400, marginBottom:'0.3rem',
            }}>
              {product.name}
            </h2>

            {product.short_description && (
              <p style={{ fontSize:'0.82rem', color:'var(--accent)', fontStyle:'italic', marginBottom:'0.8rem' }}>
                {product.short_description}
              </p>
            )}

            {longDesc && (
              <p style={{ fontSize:'0.8rem', color:'var(--muted)', lineHeight:1.75, marginBottom:'1rem' }}>
                {longDesc}
              </p>
            )}
          </div>
        </div>

        {/* Footer fixe — prix + quantité + CTA */}
        <div style={{
          padding:'1rem var(--page-px)',
          borderTop:'1px solid var(--border)',
          background:'var(--surface)',
          paddingBottom:'calc(1rem + env(safe-area-inset-bottom))',
        }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:'1rem' }}>
            <span style={{
              fontFamily:"'Cormorant Garamond',serif",
              fontSize:'1.6rem', color:'var(--accent2)',
            }}>
              {price} FCFA
            </span>
            <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:'0.15rem' }}>
              <span style={{ fontSize:'0.72rem', color: product.stock > 0 ? 'var(--success)' : 'var(--danger)' }}>
                {product.stock > 0 ? `${product.stock} en stock` : 'Rupture'}
              </span>
              {(product.min_quantity || 1) > 1 && (
                <span style={{ fontSize:'0.65rem', color:'var(--warning)', background:'rgba(212,168,87,0.1)', border:'1px solid rgba(212,168,87,0.25)', padding:'0.1rem 0.45rem', borderRadius:'2rem' }}>
                  min. {product.min_quantity} unités
                </span>
              )}
            </div>
          </div>

          <div style={{ display:'flex', gap:'0.75rem' }}>
            {/* Quantité */}
            <div style={{
              display:'flex', alignItems:'center', gap:'0',
              background:'var(--surface2)', border:'1px solid var(--border)',
              borderRadius:'2rem', overflow:'hidden', flexShrink:0,
            }}>
              <button
                onClick={() => setQty(q => Math.max(product.min_quantity || 1, q - 1))}
                style={{
                  width:'var(--touch-min)', height:'var(--touch-min)',
                  background:'none', border:'none', color:'var(--text)',
                  fontSize:'1.2rem', display:'flex', alignItems:'center', justifyContent:'center',
                }}
              >−</button>
              <span style={{ minWidth:'1.8rem', textAlign:'center', fontSize:'0.9rem', fontWeight:500 }}>{qty}</span>
              <button
                onClick={() => setQty(q => Math.min(product.stock || 99, q + 1))}
                style={{
                  width:'var(--touch-min)', height:'var(--touch-min)',
                  background:'none', border:'none', color:'var(--text)',
                  fontSize:'1.2rem', display:'flex', alignItems:'center', justifyContent:'center',
                }}
              >+</button>
            </div>

            {/* CTA */}
            <button
              onClick={handleAdd}
              disabled={product.stock === 0}
              style={{
                flex:1, height:'var(--touch-min)',
                background: added ? 'var(--success)' : 'var(--accent)',
                color:'var(--bg)', border:'none',
                borderRadius:'2rem', fontSize:'0.88rem', fontWeight:500,
                transition:'background 0.25s',
                opacity: product.stock === 0 ? 0.5 : 1,
              }}
            >
              {added
                ? '✓ Ajouté !'
                : `Ajouter${qty > 1 ? ` ×${qty}` : ''} — ${(Number(product.price) * qty).toLocaleString('fr-FR')} FCFA`
              }
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
